import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar, Footer } from '../components/Layout';
import DeviceManager from '../components/DeviceManager';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  BookOpen, Award, Trophy, Clock, Star, Play,
  Crown, Bookmark, ListVideo, ChevronRight, Smartphone, Settings,
  User, Info, Upload, Share2, MessageCircle, Copy, Check, Gift
} from 'lucide-react';
import { toast } from 'sonner';
import { categoryLabel, safeArray, coursesFromApiResponse } from '../lib/utils';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, token, isPremium, refreshUser } = useAuth();
  const { t, tFormat } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState({});
  const [certificates, setCertificates] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bio, setBio] = useState('');
  const [directoryOptIn, setDirectoryOptIn] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [shareStats, setShareStats] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [pricingSettings, setPricingSettings] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    setBio(user?.bio || '');
    setDirectoryOptIn(user?.directory_opt_in !== false);
  }, [user]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingPhoto(true);
    try {
      await axios.post(`${API_URL}/users/me/photo`, formData, { headers });
      await refreshUser();
      toast.success(t('profile_toast_photo_updated', 'Profile photo updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_toast_photo_fail', 'Failed to upload photo'));
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/auth/me`, { bio }, { headers });
      await refreshUser();
      toast.success(t('profile_toast_saved', 'Profile updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_toast_save_fail', 'Failed to update profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDirectoryToggle = async (value) => {
    setDirectoryOptIn(value);
    try {
      await axios.put(`${API_URL}/auth/me`, { directory_opt_in: value }, { headers });
      await refreshUser();
      toast.success(value
        ? t('profile_toast_directory_in', 'You now appear in the public Student Directory')
        : t('profile_toast_directory_out', 'You have been removed from the public Student Directory'));
    } catch (err) {
      setDirectoryOptIn(!value);
      toast.error(err?.response?.data?.detail || t('profile_toast_directory_fail', 'Failed to update directory preference'));
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchShareStats();
  }, [token]);

  const fetchShareStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/share/stats`, { headers });
      setShareStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCopyShareLink = () => {
    if (!shareStats?.link) return;
    navigator.clipboard.writeText(shareStats.link);
    setLinkCopied(true);
    toast.success(t('share_toast_copied', 'Link copied!'));
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleWhatsAppShare = async () => {
    if (!shareStats?.whatsapp_message) return;
    try {
      await axios.post(`${API_URL}/share/tap`, {}, { headers });
    } catch (err) { }
    const url = `https://wa.me/?text=${encodeURIComponent(shareStats.whatsapp_message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fetchData = async () => {
    try {
      const [coursesRes, certsRes, bookmarksRes, settingsRes] = await Promise.allSettled([
        axios.get(`${API_URL}/courses`, { headers }),
        axios.get(`${API_URL}/certificates`, { headers }),
        axios.get(`${API_URL}/bookmarks`, { headers }),
        axios.get(`${API_URL}/settings`)
      ]);

      const courseList = coursesRes.status === 'fulfilled' ? coursesFromApiResponse(coursesRes.value.data) : [];
      setCourses(courseList);
      setCertificates(certsRes.status === 'fulfilled' ? safeArray(certsRes.value.data) : []);
      setBookmarks(bookmarksRes.status === 'fulfilled' ? safeArray(bookmarksRes.value.data) : []);
      if (settingsRes.status === 'fulfilled') setPricingSettings(settingsRes.value.data);

      // Fetch progress for each course
      const progressData = {};
      for (const course of courseList.slice(0, 10)) {
        if (!course?.id) continue;
        try {
          const progressRes = await axios.get(`${API_URL}/progress/${course.id}`, { headers });
          const pct = progressRes.data?.progress_percent ?? 0;
          if (pct > 0) {
            progressData[course.id] = progressRes.data;
          }
        } catch (err) { }
      }
      setProgress(progressData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const inProgressCourses = safeArray(courses).filter(
    (c) => c?.id && progress[c.id] && (progress[c.id].progress_percent ?? 0) < 100
  );
  const completedCourses = safeArray(courses).filter(
    (c) => c?.id && progress[c.id] && (progress[c.id].progress_percent ?? 0) === 100
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="animate-pulse space-y-6">
              <div className="h-32 bg-muted rounded-xl" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-xl" />)}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      <Navbar />

      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-serif font-medium mb-2">
              {tFormat(
                'dash_welcome',
                { name: user?.name?.split(' ')[0] || '' },
                `Welcome back, ${user?.name?.split(' ')[0] || ''}!`
              )}
            </h1>
            <p className="text-muted-foreground">{t('dash_subtitle', 'Continue your learning journey')}</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card data-testid="stat-courses">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{Object.keys(progress).length}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('dash_stat_started', 'Courses Started')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-completed">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                    <Award className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{completedCourses.length}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('dash_stat_completed', 'Completed')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-points">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{user?.points || 0}</p>
                    <p className="text-sm text-muted-foreground">{t('dash_stat_points', 'Points')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stat-certificates">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{certificates.length}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('dash_stat_certificates', 'Certificates')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Premium Banner */}
          {!isPremium ? (
            <Card className="mb-8 bg-gradient-to-r from-accent/10 to-transparent border-accent/20">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Crown className="w-10 h-10 text-accent" />
                  <div>
                    <h3 className="font-semibold text-lg">{t('dash_upgrade_title', 'Get Premium Access')}</h3>
                    <p className="text-muted-foreground">
                      {t('dash_upgrade_desc', 'One-time payment. Get access to all courses, downloads, and certificates — forever.')}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="upgrade-btn"
                >
                  {tFormat(
                    'dash_upgrade_btn',
                    { price: `₹${Number(pricingSettings?.lifetime_price ?? 4999).toLocaleString('en-IN')}` },
                    'Get Premium Access — {price}'
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="mb-8 bg-gradient-to-r from-accent/10 to-transparent border-accent/20">
              <CardContent className="p-6 flex items-center gap-4">
                <Crown className="w-10 h-10 text-accent" />
                <div>
                  <Badge className="bg-accent text-accent-foreground mb-1">
                    {t('dash_lifetime_badge', 'Premium Access — Active ✓')}
                  </Badge>
                  <p className="text-muted-foreground">
                    {t('dash_lifetime_desc', 'You have unlimited access to all courses, forever.')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Tabs defaultValue="in-progress" className="space-y-6">
            <TabsList className="w-full max-w-full flex-nowrap justify-start overflow-x-auto scrollbar-none">
              <TabsTrigger value="in-progress" className="shrink-0">
                {t('dash_tab_progress', 'In Progress')} ({inProgressCourses.length})
              </TabsTrigger>
              <TabsTrigger value="completed" className="shrink-0">
                {t('dash_tab_completed', 'Completed')} ({completedCourses.length})
              </TabsTrigger>
              <TabsTrigger value="bookmarks" className="shrink-0">
                {t('dash_tab_bookmarks', 'Bookmarks')} ({bookmarks.length})
              </TabsTrigger>
              <TabsTrigger value="certificates" className="shrink-0">
                {t('dash_tab_certificates', 'Certificates')} ({certificates.length})
              </TabsTrigger>
              <TabsTrigger value="devices" className="flex items-center gap-2 shrink-0">
                <Smartphone className="w-4 h-4" />
                {t('dash_tab_devices', 'Devices')}
              </TabsTrigger>
              <TabsTrigger value="profile" className="flex items-center gap-2 shrink-0">
                <User className="w-4 h-4" />
                {t('dash_tab_profile', 'Profile')}
              </TabsTrigger>
              {shareStats?.enabled && (
                <TabsTrigger value="share" className="flex items-center gap-2 shrink-0">
                  <Share2 className="w-4 h-4" />
                  {t('dash_tab_share', 'Share & Earn')}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="in-progress" data-testid="in-progress-tab">
              {inProgressCourses.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Play className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {t('dash_empty_progress_title', 'No courses in progress')}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {t('dash_empty_progress_hint', 'Start learning something new today!')}
                    </p>
                    <Button onClick={() => navigate('/courses')}>
                      {t('dash_browse_courses', 'Browse Courses')}
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {inProgressCourses.map(course => (
                    <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="flex">
                        <img
                          src={course.thumbnail_url || 'https://images.unsplash.com/photo-1713652492101-4705c38e9b58'}
                          alt={course.title}
                          className="w-32 h-32 object-cover"
                        />
                        <CardContent className="flex-1 p-4">
                          <Badge variant="secondary" className="mb-2 capitalize">{categoryLabel(course.category)}</Badge>
                          <h3 className="font-semibold mb-2 line-clamp-1">{course.title}</h3>
                          <div className="space-y-2">
                            <Progress value={progress[course.id]?.progress_percent || 0} className="h-2" />
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">
                                {Math.round(progress[course.id]?.progress_percent || 0)}
                                {t('dash_percent_complete', '% complete')}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/courses/${course.id}`)}
                              >
                                {t('dash_continue', 'Continue')}
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="completed" data-testid="completed-tab">
              {completedCourses.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Award className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {t('dash_empty_done_title', 'No completed courses yet')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('dash_empty_done_hint', 'Keep learning to complete your first course!')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {completedCourses.map(course => (
                    <Card key={course.id} className="overflow-hidden">
                      <img
                        src={course.thumbnail_url || 'https://images.unsplash.com/photo-1713652492101-4705c38e9b58'}
                        alt={course.title}
                        className="w-full aspect-video object-cover"
                      />
                      <CardContent className="p-4">
                        <Badge className="bg-green-500 mb-2">{t('dash_badge_completed', 'Completed')}</Badge>
                        <h3 className="font-semibold mb-2">{course.title}</h3>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => navigate(`/courses/${course.id}`)}
                        >
                          {t('dash_review_course', 'Review Course')}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="bookmarks" data-testid="bookmarks-tab">
              {bookmarks.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Bookmark className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {t('dash_empty_bookmarks_title', 'No bookmarks yet')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('dash_empty_bookmarks_hint', 'Save lessons to revisit them later')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {bookmarks.map(lesson => (
                    <Card
                      key={lesson.id}
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/lessons/${lesson.id}`)}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                            <Bookmark className="w-5 h-5 text-accent" />
                          </div>
                          <div>
                            <h3 className="font-medium">{lesson.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {lesson.duration_minutes} {t('course_meta_min', 'min')}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="certificates" data-testid="certificates-tab">
              {certificates.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Trophy className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      {t('dash_empty_certs_title', 'No certificates yet')}
                    </h3>
                    <p className="text-muted-foreground">
                      {t('dash_empty_certs_hint', 'Complete courses to earn certificates')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {certificates.map(cert => (
                    <Card key={cert.id} className="overflow-hidden">
                      <CardContent className="p-6 text-center">
                        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Trophy className="w-8 h-8 text-accent" />
                        </div>
                        <h3 className="font-semibold mb-1">{cert.course_name}</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          {new Date(cert.completion_date).toLocaleDateString()}
                        </p>
                        <Badge variant="outline">{cert.certificate_number}</Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Device Management Tab */}
            <TabsContent value="devices" data-testid="devices-tab">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    {t('dash_devices_title', 'Device Management')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DeviceManager />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile" data-testid="profile-tab">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {t('dash_profile_title', 'My Profile')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 max-w-xl">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-20 h-20">
                      <AvatarImage src={user?.photo_url ? `${BACKEND_URL}${user.photo_url}` : undefined} alt={user?.name} />
                      <AvatarFallback className="text-2xl">{user?.name?.[0]?.toUpperCase() || '?'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <Label htmlFor="profile-photo-input" className="cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline">
                        <Upload className="w-4 h-4" />
                        {uploadingPhoto
                          ? t('profile_uploading_photo', 'Uploading...')
                          : t('profile_change_photo', 'Change photo')}
                      </Label>
                      <input
                        id="profile-photo-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto}
                        data-testid="profile-photo-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('profile_photo_hint', 'JPEG, PNG or WebP, up to 5MB. Will be cropped to a square.')}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label>{t('profile_bio_label', 'Short Bio')}</Label>
                    <Textarea
                      value={bio}
                      onChange={(e) => setBio(e.target.value)}
                      placeholder={t('profile_bio_placeholder', 'Tell other students a bit about yourself...')}
                      maxLength={300}
                      rows={4}
                      data-testid="profile-bio-input"
                    />
                  </div>

                  <Alert>
                    <Info className="w-4 h-4" />
                    <AlertDescription className="flex items-center justify-between gap-4 flex-wrap">
                      <span>
                        {t('profile_directory_banner', 'Your profile appears in our public Student Directory')}
                      </span>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={directoryOptIn}
                          onCheckedChange={handleDirectoryToggle}
                          data-testid="directory-opt-in-toggle"
                        />
                        <span className="text-sm">
                          {directoryOptIn ? t('profile_directory_on', 'Visible') : t('profile_directory_off', 'Hidden')}
                        </span>
                      </div>
                    </AlertDescription>
                  </Alert>

                  <Button onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile-btn">
                    {savingProfile ? t('profile_saving', 'Saving...') : t('profile_save', 'Save Profile')}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Share & Earn Tab */}
            {shareStats?.enabled && (
              <TabsContent value="share" data-testid="share-tab">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Share2 className="w-5 h-5" />
                      {t('share_title', 'Share & Earn')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 max-w-xl">
                    <p className="text-muted-foreground">
                      {tFormat(
                        'share_explainer',
                        { count: shareStats.shares_required, percent: shareStats.reward_percent },
                        `Share your link with friends on WhatsApp. Once ${shareStats.shares_required} friends click your link, you'll earn a ${shareStats.reward_percent}% discount coupon. Keep sharing to earn more!`
                      )}
                    </p>

                    <div>
                      <Label className="mb-2 block">{t('share_link_label', 'Your Share Link')}</Label>
                      <div className="flex gap-2 w-full overflow-hidden">
                        <input
                          readOnly
                          value={shareStats.link || ''}
                          className="flex-1 min-w-0 rounded-md border border-input bg-muted px-3 py-2 text-sm font-mono"
                          data-testid="share-link-input"
                        />
                        <Button onClick={handleCopyShareLink} variant="outline" className="shrink-0" data-testid="copy-share-link-btn">
                          {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <Button
                      onClick={handleWhatsAppShare}
                      className="bg-[#25D366] hover:bg-[#1ebe5b] text-white"
                      data-testid="whatsapp-share-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {t('share_whatsapp_btn', 'Share on WhatsApp')}
                    </Button>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>{t('share_progress_label', 'Verified Shares')}</Label>
                        <span className="text-sm font-medium" data-testid="share-progress-text">
                          {shareStats.verified_count} / {shareStats.shares_required}
                        </span>
                      </div>
                      <div className="w-full overflow-hidden">
                        <Progress
                          value={Math.min(100, (shareStats.verified_count / shareStats.shares_required) * 100)}
                          className="h-2 w-full"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('share_progress_hint', 'A "verified share" counts when a friend who clicked your link visits the site for the first time. Raw WhatsApp button taps are tracked separately and don\'t count toward your reward.')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {tFormat(
                          'share_taps_hint',
                          { count: shareStats.taps_count },
                          `Friends who clicked your link: ${shareStats.taps_count}`
                        )}
                      </p>
                    </div>

                    {shareStats.max_rewards_per_user_month > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {tFormat(
                          'share_monthly_cap',
                          { used: shareStats.rewards_this_month, max: shareStats.max_rewards_per_user_month },
                          `Rewards earned this month: ${shareStats.rewards_this_month} / ${shareStats.max_rewards_per_user_month}`
                        )}
                      </p>
                    )}

                    {shareStats.rewards?.length > 0 && (
                      <div>
                        <Label className="mb-2 block flex items-center gap-2">
                          <Gift className="w-4 h-4" />
                          {t('share_rewards_label', 'Your Reward Coupons')}
                        </Label>
                        <div className="space-y-2">
                          {shareStats.rewards.map((r) => (
                            <div key={r.id} className="flex items-center justify-between rounded-md border border-input px-3 py-2 text-sm">
                              <span className="font-mono font-medium">{r.coupon_code}</span>
                              <span className="text-muted-foreground">
                                {r.reward_percent}% {t('share_off', 'off')} &middot; {t('share_expires', 'expires')} {new Date(r.expires_at).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>

          {/* Badges */}
          {user?.badges?.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-serif font-medium mb-6">
                {t('dash_badges_heading', 'Your Badges')}
              </h2>
              <div className="flex flex-wrap gap-4">
                {user.badges.map(badge => (
                  <Badge key={badge} variant="outline" className="px-4 py-2 text-base">
                    <Star className="w-4 h-4 mr-2 text-amber-500" />
                    {badge.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UserDashboard;
